import type { ContainerStyles, IntentTokens } from './button.types'

// ---------------------------------------------------------------------------
// Generated variant approach
//
// Each intent defines its token references once.
// Each hierarchy rule composes container styles from those tokens.
// The 12 canonical variants are produced via Object.fromEntries — no
// hand-written duplication; adding a new intent or hierarchy is a 1-line change.
// ---------------------------------------------------------------------------

/** Token references per semantic intent. */
const intentTokens: Record<string, IntentTokens> = {
  brand: {
    solid: 'var(--vui-action-brand-solid)',
    solidInverse: 'var(--vui-action-brand-solid-inverse)',
    solidHover: 'var(--vui-action-brand-solid-hover)',
    solidPressed: 'var(--vui-action-brand-solid-pressed)',
    subtleHover: 'var(--vui-action-brand-subtle-hover)',
    subtlePressed: 'var(--vui-action-brand-subtle-pressed)',
  },
  contrast: {
    solid: 'var(--vui-action-contrast-solid)',
    solidInverse: 'var(--vui-action-contrast-solid-inverse)',
    solidHover: 'var(--vui-action-contrast-solid-hover)',
    solidPressed: 'var(--vui-action-contrast-solid-pressed)',
    subtleHover: 'var(--vui-action-contrast-subtle-hover)',
    subtlePressed: 'var(--vui-action-contrast-subtle-pressed)',
  },
  success: {
    solid: 'var(--vui-action-success-solid)',
    solidInverse: 'var(--vui-action-success-solid-inverse)',
    solidHover: 'var(--vui-action-success-solid-hover)',
    solidPressed: 'var(--vui-action-success-solid-pressed)',
    subtleHover: 'var(--vui-action-success-subtle-hover)',
    subtlePressed: 'var(--vui-action-success-subtle-pressed)',
  },
  danger: {
    solid: 'var(--vui-action-danger-solid)',
    solidInverse: 'var(--vui-action-danger-solid-inverse)',
    solidHover: 'var(--vui-action-danger-solid-hover)',
    solidPressed: 'var(--vui-action-danger-solid-pressed)',
    subtleHover: 'var(--vui-action-danger-subtle-hover)',
    subtlePressed: 'var(--vui-action-danger-subtle-pressed)',
  },
  warning: {
    solid: 'var(--vui-action-yellow-solid)',
    solidInverse: 'var(--vui-action-yellow-solid-inverse)',
    solidHover: 'var(--vui-action-yellow-solid-hover)',
    solidPressed: 'var(--vui-action-yellow-solid-pressed)',
    subtleHover: 'var(--vui-action-yellow-subtle-hover)',
    subtlePressed: 'var(--vui-action-yellow-subtle-pressed)',
  },
}

const TRANSPARENT = 'var(--vui-color-opacity-none)'
const DISABLED_BG = 'var(--vui-action-disabled-background)'
const DISABLED_FG = 'var(--vui-action-disabled-foreground)'

/** Container styles per visual hierarchy, composed from intent tokens. */
const hierarchyRules: Record<string, (t: IntentTokens) => ContainerStyles> = {
  primary: t => ({
    bg: t.solid,
    borderColor: t.solid,
    color: t.solidInverse,
    hoverBg: t.solidHover,
    hoverBorderColor: t.solidHover,
    activeBg: t.solidPressed,
    activeBorderColor: t.solidPressed,
    disabledBg: DISABLED_BG,
    disabledBorderColor: DISABLED_BG,
    disabledColor: DISABLED_FG,
  }),
  secondary: t => ({
    bg: TRANSPARENT,
    borderColor: t.solid,
    color: t.solid,
    hoverBg: t.subtleHover,
    activeBg: t.subtlePressed,
    disabledBg: TRANSPARENT,
    disabledBorderColor: DISABLED_BG,
    disabledColor: DISABLED_FG,
  }),
  tertiary: t => ({
    bg: TRANSPARENT,
    borderColor: TRANSPARENT,
    color: t.solid,
    hoverBg: t.subtleHover,
    activeBg: t.subtlePressed,
    disabledBg: TRANSPARENT,
    disabledBorderColor: TRANSPARENT,
    disabledColor: DISABLED_FG,
  }),
}

// ---------------------------------------------------------------------------

const baseStyle = {}

const defaultProps = {
  size: 'lg',
  variant: 'primaryBrand',
  fontWeight: 'medium',
}

const parts = ['container', 'icon', 'text']

// Padding model (applied in useButtonComputed.ts):
// - Horizontal uses 2 tiers via theme tokens:
//   text-only => paddingX
//   icon+text => paddingXIcon (fallback: paddingX)
//   icon-only => no padding (centering via fixed w=h)
// - Vertical token paddingY is border-box aware at runtime:
//   effective pt/pb = paddingY - borderWidth
const sizes = {
  sm: {
    container: {
      fontSize: 'xs',
      h: 24,
      paddingX: 12,
      paddingXIcon: 8,
      paddingY: 5,
      gapX: 8,
    },
    icon: {
      fontSize: '12px',
    },
  },
  md: {
    container: {
      fontSize: 'sm',
      h: 32,
      paddingX: 16,
      paddingXIcon: 12,
      paddingY: 8,
      gapX: 8,
    },
    icon: {
      fontSize: '16px',
    },
  },
  lg: {
    container: {
      fontSize: 'md',
      h: 40,
      paddingX: 16,
      paddingXIcon: 16,
      paddingY: 10,
      gapX: 8,
    },
    icon: {
      fontSize: '16px',
    },
  },
  xl: {
    container: {
      fontSize: 'lg',
      h: 48,
      paddingX: 16,
      paddingXIcon: 16,
      paddingY: 13,
      gapX: 8,
    },
    icon: {
      fontSize: '18px',
    },
  },
}

const variants: Record<string, Record<string, ContainerStyles>> = Object.fromEntries(
  Object.entries(intentTokens).flatMap(([intentKey, tokens]) =>
    Object.entries(hierarchyRules).map(([hierarchyKey, buildContainer]) => [
      `${hierarchyKey}${intentKey.charAt(0).toUpperCase()}${intentKey.slice(1)}`,
      { container: buildContainer(tokens) },
    ]),
  ),
)

// Deprecated variant aliases for backward compatibility - will be removed in VUI 5.x
variants.primaryDark = variants.primaryBrand
variants.secondaryDark = variants.secondaryBrand
variants.tertiaryDark = variants.tertiaryBrand
variants.primaryLight = variants.primaryContrast
variants.secondaryLight = variants.secondaryContrast
variants.tertiaryLight = variants.tertiaryContrast
variants.solidGreen = variants.primarySuccess
variants.solidRed = variants.primaryDanger
variants.solidDarkBlue = variants.tertiaryBrand
variants.solidYellow = variants.tertiaryWarning

// Legacy blue variants (removed in VUI 4.0, added back as deprecated aliases)
variants.solidBlue = variants.primaryBrand
variants.blueOutlined = variants.secondaryBrand
variants.blueText = variants.tertiaryBrand

// Component-specific subtle variants (removed in VUI 4.0, added back as deprecated aliases)
variants.subtleBlue = variants.secondaryBrand
variants.subtleRed = variants.secondaryDanger
variants.subtleGreen = variants.secondarySuccess
variants.subtleYellow = variants.secondaryWarning
variants.subtleGrey = variants.tertiaryBrand

// Menu-specific variants (removed in VUI 4.0, added back as deprecated aliases)
variants.menuLight = variants.tertiaryBrand
variants.menuDark = variants.tertiaryBrand

// buttonGroupDefault is an internal variant, used in ButtonGroup; based on secondaryBrand
variants.buttonGroupDefault = {
  container: {
    bg: 'var(--vui-color-opacity-none)',
    borderColor: 'var(--vui-control-placeholder)',
    color: 'var(--vui-action-brand-solid)',
    hoverBg: 'var(--vui-action-brand-subtle-hover)',
    activeBg: 'var(--vui-action-brand-subtle-pressed)',
  },
}

// buttonGroupRounded is an internal variant
variants.buttonGroupRounded = {
  container: {
    bg: 'var(--vui-background-default)',
    borderColor: 'var(--vui-utility-border-subtle)',
    color: 'var(--vui-foreground-default)',
    hoverBg: 'var(--vui-action-brand-subtle-hover)',
    hoverBorderColor: 'var(--vui-action-brand-subtle-hover)',
    hoverColor: 'var(--vui-action-brand-subtle-inverse)',
    activeBg: 'var(--vui-action-brand-solid-pressed)',
    activeBorderColor: 'var(--vui-action-brand-solid-pressed)',
    activeColor: 'var(--vui-action-brand-solid-inverse)',
    borderRadius: '1000',
    marginRight: 'var(--vui-button-group-rounded-spacing, 22px)',
  },
}

export default {
  baseStyle,
  defaultProps,
  parts,
  sizes,
  variants,
}
